import { ReactNode, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ErrorVariant } from '@percent/domain/verifications'
import { Loader } from '@percent/lemonade'
import { ProgressContextController } from '@percent/product-nomination-form/context/progress/ProgressContextController/ProgressContextController'
import { useNavigationStack } from '@percent/product-nomination-form/hooks/useNavigationStack/useNavigationStack'
import { ErrorCard } from '@percent/product-nomination-form/ui/ErrorCard/ErrorCard'
import { HostedFormProps } from '../HostedForm.types'
import { isAfter } from 'date-fns'
import { SubmitNominationFormPayload } from '@percent/product-nomination-form/api/nominationForm/nominationForm.types'
import { HostedFormStep } from './HostedForm.types'
import {
  Complete,
  Introduction,
  NominatorDetails,
  Nominees,
  OrganisationOrigin,
  OrganisationResult,
  RegisterOrganisationContainer,
  SelectOrganisationContainer,
  TermsContainer,
} from './steps'
import { usePartnerConfig } from '@percent/product-nomination-form/hooks'

const steps: { [key in HostedFormStep]: ReactNode } = {
  [HostedFormStep.INTRODUCTION]: <Introduction />,
  [HostedFormStep.NOMINATOR_DETAILS]: <NominatorDetails />,
  [HostedFormStep.ORGANISATION_ORIGIN]: <OrganisationOrigin />,
  [HostedFormStep.SELECT_ORGANISATION]: <SelectOrganisationContainer />,
  [HostedFormStep.REGISTER_ORGANISATION]: <RegisterOrganisationContainer />,
  [HostedFormStep.ORGANISATION_RESULT]: <OrganisationResult />,
  [HostedFormStep.NOMINEES]: <Nominees />,

  [HostedFormStep.TERMS]: <TermsContainer />,
  [HostedFormStep.COMPLETE]: <Complete />,
}

export const HostedForm = ({
  isError,
  isInternalError,
  isLoading,
  isBlockedCookies,
}: HostedFormProps) => {
  const { id } = useParams<{ id: string }>()
  const { validationStep } = useNavigationStack()
  const { t } = useTranslation()
  const methods = useFormContext<SubmitNominationFormPayload>()
  const { partnerConfig } = usePartnerConfig()

  useEffect(() => {
    if (partnerConfig?.productNominationConfigurationId) {
      methods.setValue(
        'productNominationConfigurationId',
        partnerConfig?.productNominationConfigurationId
      )
    }

    if (id) {
      methods.setValue('productNominationInviteId', id)
    }
  }, [id, partnerConfig?.productNominationConfigurationId])

  const isLinkExpired =
    partnerConfig?.expiresAt &&
    isAfter(new Date(), new Date(partnerConfig.expiresAt))

  return (
    <ProgressContextController>
      {isLoading && <Loader />}
      {!isLoading && partnerConfig?.status === 'complete' && !isLinkExpired && (
        <ErrorCard
          title={t('hostedValidation.errorMessage.linkUsedTitle')}
          subtitle={t('hostedValidation.errorMessage.linkUsedSubtitle')}
          variant={ErrorVariant.LINK_ALREADY_USED}
        />
      )}
      {!isLoading && isInternalError && !isError && !isBlockedCookies && (
        <ErrorCard
          title={t('hostedValidation.errorMessage.somethingWentWrong')}
          subtitle={t('hostedValidation.errorMessage.reloadPage')}
          variant={ErrorVariant.INTERNAL}
        />
      )}
      {!isLoading && isError && !isInternalError && !isBlockedCookies && (
        <ErrorCard
          variant={ErrorVariant.WRONG_LINK}
          title={t('hostedValidation.errorMessage.invalidLink')}
          subtitle={t('hostedValidation.errorMessage.linkDoesntExist')}
        />
      )}
      {!isLoading && isLinkExpired && (
        <ErrorCard
          variant={ErrorVariant.WRONG_LINK}
          title={t('hostedValidation.errorMessage.invalidLink')}
          subtitle={t('hostedValidation.errorMessage.linkDoesntExist')}
        />
      )}
      {!isLoading &&
        !isError &&
        !isInternalError &&
        !isLinkExpired &&
        isBlockedCookies && (
          <ErrorCard
            variant={ErrorVariant.BLOCKED_COOKIES}
            title={t('hostedValidation.errorMessage.blockedCookies')}
            subtitle={t('hostedValidation.errorMessage.allowCookies')}
          />
        )}
      {!isLoading &&
        partnerConfig?.status === 'open' &&
        !isLinkExpired &&
        !isError &&
        steps[validationStep]}
    </ProgressContextController>
  )
}
